import { useAppNavigate } from "hooks/useAppNavigate";
import React, { useState } from "react";
import { useLocation } from "react-router-dom";
import LoadingButton from "components/LoadingButton";
import ErrorMessage from "components/ErrorMessage";
import PageTitle from "components/PageTitle";
import PageMeta from "components/PageMeta";
import CheckboxField from "components/CheckboxField";
import LegalLink from "components/LegalLink";
import { updateUserProfile, approveDeviceCode, isServiceUnavailableError } from "services/auth.service";
import { getUserData, setUserData, getFlowState, clearFlowState } from "../../storage";
import { useLoginPage } from "hooks/useLoginPage";
import TextInput from "components/TextInput";
import { UserData } from "types";
import { isValidPassword } from "utils/validators";
import { postAuthTokenToNative } from "utils/nativeBridge";
import { useText } from "context/TextProvider";
import { trackEvent } from "services/analytics.service";
import { AnalyticsEvent, AnalyticsFlow, AnalyticsStatus, DeviceGrantStep } from "services/analyticsEvents";
import { useScreenTimeTracking } from "hooks/useScreenTimeTracking";

const DeviceGrantProfileUpdate = (): JSX.Element => {
  const text = useText();
  const state = useLocation().state as { email?: string } | null;
  const appNavigate = useAppNavigate();
  const { configId, loginPageConfig } = useLoginPage();
  useScreenTimeTracking(AnalyticsEvent.TIME_ON_DEVICE_PROFILE_UPDATE_SCREEN);
  const email = state?.email || getFlowState(configId).email || "";
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [agreeTerms, setAgreeTerms] = useState(false);
  const [sendEmails, setSendEmails] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!firstName) {
      setError(text.deviceGrant.profileUpdate.firstNameRequired);
      return;
    }
    if (!lastName) {
      setError(text.deviceGrant.profileUpdate.lastNameRequired);
      return;
    }
    if (!password) {
      setError(text.deviceGrant.profileUpdate.newPasswordRequired);
      return;
    }
    if (!isValidPassword(password)) {
      setError(text.deviceGrant.profileUpdate.passwordRequirements);
      return;
    }
    setError("");
    setLoading(true);
    try {
      const userData = getUserData();
      if (!userData) return;
      const userId = userData.user.id;
      const updatedUser = await updateUserProfile({
        email,
        firstName,
        lastName,
        password,
        userId,
        url: loginPageConfig?.urls?.update_profile,
        accessToken: userData.token,
        approveMarketing: agreeTerms ? "ON" : "OFF",
        approveTermsOfUse: sendEmails ? "ON" : "OFF",
      });
      setUserData({
        ...userData,
        user: updatedUser.user,
      });
      const data = getUserData();
      if (!data) return;
      trackEvent(AnalyticsEvent.DEVICE_PROFILE_UPDATE_SUCCEEDED, { flow: AnalyticsFlow.DEVICE_GRANT, status: AnalyticsStatus.SUCCEEDED });
      const userCode = sessionStorage.getItem("device_grant_user_code") ?? undefined;
      const jwt = data.token;
      await approveDeviceCode({
        user_code: userCode,
        access_token: jwt,
        url: loginPageConfig?.urls?.approve_device_code,
      });
      trackEvent(AnalyticsEvent.APPROVE_DEVICE_CODE_SUCCEEDED, { step: DeviceGrantStep.APPROVE_DEVICE_CODE, status: AnalyticsStatus.SUCCEEDED });
      postAuthTokenToNative({
        token: data.token,
        refreshToken: data.refreshToken,
        tokenExpirationInstant: data.tokenExpirationInstant,
        refreshUrl: loginPageConfig?.urls?.refresh_url,
      });
      clearFlowState(configId);
      appNavigate(`/device-grant-flow/complete`);
    } catch (err) {
      setError(isServiceUnavailableError(err) ? text.common.serviceUnavailable : text.deviceGrant.profileUpdate.updateFailed);
      trackEvent(AnalyticsEvent.DEVICE_PROFILE_UPDATE_FAILED, { flow: AnalyticsFlow.DEVICE_GRANT, status: AnalyticsStatus.FAILED });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <PageMeta name="Device Grant Profile Update" />
      <PageTitle>{text.deviceGrant.profileUpdate.title}</PageTitle>
      {error && <ErrorMessage>{error}</ErrorMessage>}
      <TextInput
        id="firstName"
        type="text"
        label={text.deviceGrant.profileUpdate.firstNameLabel}
        value={firstName}
        placeholder={text.deviceGrant.profileUpdate.firstNamePlaceholder}
        onChange={(e) => setFirstName(e.target.value)}
      />
      <TextInput
        id="lastName"
        type="text"
        label={text.deviceGrant.profileUpdate.lastNameLabel}
        value={lastName}
        placeholder={text.deviceGrant.profileUpdate.lastNamePlaceholder}
        onChange={(e) => setLastName(e.target.value)}
      />
      <TextInput
        id="newPassword"
        type="password"
        label={text.deviceGrant.profileUpdate.newPasswordLabel}
        value={password}
        placeholder={text.deviceGrant.profileUpdate.newPasswordPlaceholder}
        onChange={(e) => setPassword(e.target.value)}
      />
      <CheckboxField
        id="agreeTerms"
        checked={agreeTerms}
        onChange={(e) => setAgreeTerms(e.target.checked)}
        label={
          <>
            {text.deviceGrant.profileUpdate.agreeTermsLabel}{" "}
            <LegalLink href={loginPageConfig?.login_page_style?.termsAndConditionsUrl}>
              {text.deviceGrant.profileUpdate.termsAndConditionsLinkText}
            </LegalLink>
          </>
        }
      />
      <CheckboxField
        id="sendEmails"
        checked={sendEmails}
        onChange={(e) => setSendEmails(e.target.checked)}
        label={text.deviceGrant.profileUpdate.sendEmailsLabel}
      />
      <LoadingButton
        loading={loading}
        onClick={handleSubmit}
        sx={{
          width: "100%",
          height: "3.5rem",
        }}
        disabled={loading || !agreeTerms}
      >
        {text.deviceGrant.profileUpdate.updateButton}
      </LoadingButton>
    </>
  );
};

export default DeviceGrantProfileUpdate;
